import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Image as ImageIcon, Plus, Trash2, Save, Eye, EyeOff, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899"];
const KINDS = [
  { value: "issue", label: "Issue", color: "#ef4444" },
  { value: "callout", label: "Callout", color: "#f59e0b" },
  { value: "complete", label: "Completed", color: "#10b981" },
  { value: "measurement", label: "Measurement", color: "#3b82f6" },
  { value: "note", label: "Note", color: "#8b5cf6" },
];

export default function JobPhotos() {
  const [schedules, setSchedules] = useState([]);
  const [selectedSched, setSelectedSched] = useState("");
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [tool, setTool] = useState("note");
  const [drawing, setDrawing] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(null);
  const imgRef = useRef(null);

  const loadSchedules = async () => {
    try { const r = await axios.get(`${API}/schedules`); setSchedules(r.data?.items || r.data || []); }
    catch { /* silent */ }
  };
  const loadPhotos = async (sid) => {
    try { const r = await axios.get(`${API}/job-photos?schedule_id=${sid}`); setPhotos(r.data || []); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { loadSchedules(); }, []);
  useEffect(() => { if (selectedSched) loadPhotos(selectedSched); }, [selectedSched]);

  const openPhoto = (p) => { setActive(p); setAnnotations(p.annotations || []); };

  const startDraw = (e) => {
    if (!imgRef.current || !active) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setDrawing({ x, y, w: 0, h: 0, label: "", kind: tool, color: KINDS.find((k) => k.value === tool)?.color });
  };
  const moveDraw = (e) => {
    if (!drawing || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setDrawing({ ...drawing, w: x - drawing.x, h: y - drawing.y });
  };
  const endDraw = () => {
    if (!drawing) return;
    if (Math.abs(drawing.w) < 0.02 || Math.abs(drawing.h) < 0.02) { setDrawing(null); return; }
    // Normalize negative w/h
    const ann = { ...drawing };
    if (ann.w < 0) { ann.x += ann.w; ann.w = -ann.w; }
    if (ann.h < 0) { ann.y += ann.h; ann.h = -ann.h; }
    const label = window.prompt("Label?", "") || "";
    setAnnotations([...annotations, { ...ann, label }]);
    setDrawing(null);
  };
  const removeAnn = (i) => setAnnotations(annotations.filter((_, j) => j !== i));

  const saveAnn = async () => {
    if (!active) return;
    try {
      await axios.put(`${API}/job-photos/${active.id}/annotations`, { annotations });
      toast.success("Annotations saved");
      loadPhotos(selectedSched);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const changeVisibility = async (val) => {
    try {
      await axios.put(`${API}/job-photos/${active.id}`, { ...active, annotations, visibility: val });
      toast.success(`Visibility: ${val}`);
      setActive({ ...active, visibility: val });
      loadPhotos(selectedSched);
    } catch { toast.error("Update failed"); }
  };

  const doUpload = async () => {
    if (!uploadDialog?.image_url || !uploadDialog?.schedule_id) { toast.error("URL + job required"); return; }
    try {
      await axios.post(`${API}/job-photos`, { ...uploadDialog, annotations: [] });
      toast.success("Photo added"); setUploadDialog(null); loadPhotos(selectedSched);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Delete photo?")) return;
    try { await axios.delete(`${API}/job-photos/${id}`); if (active?.id === id) setActive(null); loadPhotos(selectedSched); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4 animate-in" data-testid="job-photos-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><ImageIcon className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Job Photos & Annotation</h1>
          <p className="text-muted-foreground">Click-and-drag rectangles to label issues, measurements, callouts.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <div className="flex gap-2 items-center">
            <Label>Job</Label>
            <Select value={selectedSched} onValueChange={setSelectedSched}>
              <SelectTrigger className="w-64" data-testid="schedule-select"><SelectValue placeholder="Pick a job" /></SelectTrigger>
              <SelectContent>{schedules.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {selectedSched && <Button onClick={() => setUploadDialog({ schedule_id: selectedSched, image_url: "", title: "", description: "", visibility: "admin_only" })} data-testid="upload-btn"><Plus className="h-4 w-4 mr-1" /> Add photo</Button>}
        </CardHeader>
        <CardContent>
          {!selectedSched ? <p className="text-muted-foreground py-8 text-center">Pick a job to see its photos.</p> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="photo-grid">
              {photos.map((p) => (
                <Card key={p.id} className={`cursor-pointer ${active?.id === p.id ? "border-primary" : ""}`} onClick={() => openPhoto(p)} data-testid={`photo-${p.id}`}>
                  <div className="aspect-video bg-muted overflow-hidden rounded-t-lg">
                    <img src={p.thumbnail_url || p.image_url} className="w-full h-full object-cover" alt={p.title} />
                  </div>
                  <CardContent className="p-2">
                    <p className="text-xs font-medium truncate">{p.title || "Untitled"}</p>
                    <div className="flex justify-between mt-1">
                      <Badge variant="outline" className="text-[10px]">{p.visibility}</Badge>
                      <Badge variant="outline" className="text-[10px]">{(p.annotations || []).length} ann</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {photos.length === 0 && <p className="col-span-full text-muted-foreground text-center py-8">No photos yet.</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ANNOTATION CANVAS */}
      {active && (
        <Card data-testid="annotation-canvas">
          <CardHeader className="flex flex-row justify-between items-center flex-wrap gap-2">
            <div>
              <CardTitle>{active.title || "Untitled photo"}</CardTitle>
              <CardDescription>Drag on the image to draw a rectangle. Click any annotation to delete.</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-muted-foreground mr-1">Tool:</span>
              {KINDS.map((k) => (
                <button key={k.value} onClick={() => setTool(k.value)}
                  className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${tool === k.value ? "border-primary bg-primary/10" : ""}`}
                  data-testid={`tool-${k.value}`}>
                  <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: k.color }} /> {k.label}
                </button>
              ))}
              <div className="border-l h-6 mx-1" />
              <Select value={active.visibility} onValueChange={changeVisibility}>
                <SelectTrigger className="w-36 h-8" data-testid="visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin_only">Admin only</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={saveAnn} data-testid="save-annotations-btn"><Save className="h-4 w-4 mr-1" />Save</Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deletePhoto(active.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="relative inline-block max-w-full" onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={() => setDrawing(null)}>
              <img ref={imgRef} src={active.image_url} className="max-w-full max-h-[60vh] block cursor-crosshair select-none" draggable={false} onMouseDown={startDraw} alt={active.title || "Photo"} />
              {/* Existing annotations */}
              {annotations.map((a, i) => (
                <div key={i} className="absolute group" style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%`, width: `${a.w * 100}%`, height: `${a.h * 100}%`, border: `2px solid ${a.color || "#ef4444"}`, boxShadow: `0 0 0 1px rgba(0,0,0,0.3) inset` }} data-testid={`annotation-${i}`}>
                  <button onClick={() => removeAnn(i)} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                  {a.label && <span className="absolute -top-5 left-0 text-[10px] px-1 rounded whitespace-nowrap" style={{ backgroundColor: a.color || "#ef4444", color: "#fff" }}>{a.label}</span>}
                </div>
              ))}
              {/* Currently drawing */}
              {drawing && (
                <div className="absolute pointer-events-none" style={{
                  left: `${Math.min(drawing.x, drawing.x + drawing.w) * 100}%`,
                  top: `${Math.min(drawing.y, drawing.y + drawing.h) * 100}%`,
                  width: `${Math.abs(drawing.w) * 100}%`, height: `${Math.abs(drawing.h) * 100}%`,
                  border: `2px dashed ${drawing.color}`,
                }} />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{annotations.length} annotation(s). Click an existing rectangle&apos;s × to remove.</p>
          </CardContent>
        </Card>
      )}

      {/* Upload dialog */}
      <Dialog open={!!uploadDialog} onOpenChange={(o) => !o && setUploadDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add photo</DialogTitle><DialogDescription>Paste an image URL (use Object Storage uploader for production).</DialogDescription></DialogHeader>
          {uploadDialog && (
            <div className="space-y-3">
              <div><Label>Image URL</Label><Input value={uploadDialog.image_url} onChange={(e) => setUploadDialog({ ...uploadDialog, image_url: e.target.value })} placeholder="https://..." data-testid="image-url-input" /></div>
              <div><Label>Title</Label><Input value={uploadDialog.title} onChange={(e) => setUploadDialog({ ...uploadDialog, title: e.target.value })} data-testid="photo-title-input" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={uploadDialog.description} onChange={(e) => setUploadDialog({ ...uploadDialog, description: e.target.value })} /></div>
              <div>
                <Label>Visibility</Label>
                <Select value={uploadDialog.visibility} onValueChange={(v) => setUploadDialog({ ...uploadDialog, visibility: v })}>
                  <SelectTrigger data-testid="upload-visibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin_only">Admin only</SelectItem>
                    <SelectItem value="employee">Employee portal</SelectItem>
                    <SelectItem value="client">Client portal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(null)}>Cancel</Button>
            <Button onClick={doUpload} data-testid="confirm-upload-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
